/**
 * Pure sector-aware Balance Sheet benchmark evaluation.
 * Scoring lives here, not in React. Inputs are copied and never mutated.
 */

import {
    ARCHETYPE_THRESHOLDS,
    BALANCE_SHEET_NOT_APPLICABLE_REASONS,
    INVENTORY_NOT_APPLICABLE_ARCHETYPES,
    INVENTORY_NOT_APPLICABLE_NOTE,
    METRIC_LABELS,
    SCORED_METRIC_IDS,
} from '../config/balanceSheetBenchmarkConfig';
import {
    BALANCE_SHEET_RATIOS_PANEL_MAP,
} from '../config/balanceSheetRatiosPanelBenchmarkMap';
import {
    BENCHMARK_SOURCE_HEURISTIC,
    FALLBACK_SECTOR_NOTE,
    METHODOLOGY_NOTE,
} from '../config/sectorTaxonomy';
import {
    applyThreshold,
    calculatePositiveYearsRatio,
    collectAnnualPoints,
    copyAligned,
    formatThresholdRule,
    isVolatile,
    median,
} from './benchmarkMath';
import {
    buildSectorBenchmarkProfile,
    isNotApplicableSector,
} from './sectorResolver';

export function getBalanceSheetBenchmarkProfile(rawSector) {
    return buildSectorBenchmarkProfile(rawSector, ARCHETYPE_THRESHOLDS, {
        notApplicableReasons: BALANCE_SHEET_NOT_APPLICABLE_REASONS,
    });
}

function metricLabel(metricId) {
    return METRIC_LABELS[metricId] || metricId;
}

function emptyEvaluation(overrides) {
    return {
        metricId: overrides.metricId,
        label: metricLabel(overrides.metricId),
        status: overrides.status,
        latestStatus: overrides.latestStatus || null,
        primaryValue: overrides.primaryValue ?? null,
        latestValue: overrides.latestValue ?? null,
        median3: overrides.median3 ?? null,
        median5: overrides.median5 ?? null,
        min: overrides.min ?? null,
        max: overrides.max ?? null,
        validAnnualCount: overrides.validAnnualCount || 0,
        positiveYearCount: overrides.positiveYearCount || 0,
        positiveYearRatio: overrides.positiveYearRatio ?? null,
        windowUsed: overrides.windowUsed || null,
        ruleText: overrides.ruleText || '',
        interpretation: overrides.interpretation || '',
        warnings: overrides.warnings || [],
        volatilityWarning: Boolean(overrides.volatilityWarning),
        source: BENCHMARK_SOURCE_HEURISTIC,
        unit: overrides.unit || null,
        notApplicableReason: overrides.notApplicableReason || null,
    };
}

function buildInterpretation(metricId, status, profile, extras = {}) {
    const sectorLabel = profile.sector.label;
    const label = metricLabel(metricId);
    if (status === 'notApplicable') {
        return extras.notApplicableReason || profile.notApplicableReason || `${label} is not applicable for this company type.`;
    }
    if (status === 'insufficientData') {
        return `Fewer than ${profile.requiredWindow} valid annual observations are available for a ${sectorLabel} sector score. Latest year is shown as context only.`;
    }
    if (status === 'strong') return `Multi-year ${label} meets the initial ${sectorLabel} screening threshold.`;
    if (status === 'watch') return `Multi-year ${label} is outside the strong ${sectorLabel} band and warrants explanation.`;
    return `Multi-year ${label} is outside the ${sectorLabel} screening band and requires investigation.`;
}

function isInventoryNotApplicable(sector) {
    if (!sector) return false;
    return isNotApplicableSector(sector)
        || INVENTORY_NOT_APPLICABLE_ARCHETYPES.has(sector.archetypeId)
        || sector.sectorId === 'internetDigitalPlatforms';
}

export function evaluatePerYearBalanceSheetStatus({ metricId, sector, values, years } = {}) {
    const profile = getBalanceSheetBenchmarkProfile(sector);
    if (metricId === 'inventoryTurnoverRatio' && isInventoryNotApplicable(profile.sector)) {
        return [];
    }
    const spec = metricId ? profile.metrics?.[metricId] : null;
    const copied = copyAligned(values, years);
    return collectAnnualPoints(copied.values, copied.years).map((point) => ({
        year: point.year,
        value: point.value,
        status: applyThreshold(point.value, spec),
    }));
}

export function evaluateBalanceSheetMetric({
    metricId,
    sector,
    values,
    years,
} = {}) {
    const profile = getBalanceSheetBenchmarkProfile(sector);
    const copied = copyAligned(values, years);
    const warnings = [];
    if (profile.sector.unmapped) warnings.push(FALLBACK_SECTOR_NOTE);

    if (!metricId) {
        return emptyEvaluation({
            metricId: 'unknown',
            status: 'insufficientData',
            interpretation: 'No metric was supplied for evaluation.',
            warnings,
        });
    }

    if (isNotApplicableSector(profile.sector)) {
        return emptyEvaluation({
            metricId,
            status: 'notApplicable',
            notApplicableReason: profile.notApplicableReason,
            interpretation: profile.notApplicableReason,
            windowUsed: profile.primaryWindowLabel,
            warnings,
        });
    }

    if (metricId === 'inventoryTurnoverRatio' && isInventoryNotApplicable(profile.sector)) {
        return emptyEvaluation({
            metricId,
            status: 'notApplicable',
            notApplicableReason: INVENTORY_NOT_APPLICABLE_NOTE,
            interpretation: INVENTORY_NOT_APPLICABLE_NOTE,
            windowUsed: profile.primaryWindowLabel,
            warnings,
        });
    }

    const spec = profile.metrics?.[metricId];
    if (!spec) {
        return emptyEvaluation({
            metricId,
            status: 'notApplicable',
            notApplicableReason: `${metricLabel(metricId)} has no sector-aware balance-sheet benchmark.`,
            interpretation: `${metricLabel(metricId)} has no sector-aware balance-sheet benchmark.`,
            warnings,
        });
    }

    const ruleText = formatThresholdRule(metricId, spec, profile.requiredWindow, {
        label: metricLabel(metricId),
    });
    const annual = collectAnnualPoints(copied.values, copied.years);
    const annualValues = annual.map((point) => point.value);
    const latestPoint = annual.length ? annual[annual.length - 1] : null;
    const latestValue = latestPoint ? latestPoint.value : null;
    const median3 = annual.length >= 3 ? median(annual.slice(-3).map((point) => point.value)) : null;
    const median5 = annual.length >= 5 ? median(annual.slice(-5).map((point) => point.value)) : null;
    const primaryValue = profile.requiredWindow === 5 ? median5 : median3;
    const min = annualValues.length ? Math.min(...annualValues) : null;
    const max = annualValues.length ? Math.max(...annualValues) : null;
    const positiveYearCount = annualValues.filter((value) => value > 0).length;
    const positiveYearRatio = calculatePositiveYearsRatio(annualValues);
    const latestStatus = applyThreshold(latestValue, spec);

    if (annual.length < profile.requiredWindow || primaryValue == null) {
        return emptyEvaluation({
            metricId,
            status: 'insufficientData',
            latestStatus,
            latestValue,
            primaryValue: null,
            median3,
            median5,
            min,
            max,
            validAnnualCount: annual.length,
            positiveYearCount,
            positiveYearRatio,
            windowUsed: profile.primaryWindowLabel,
            ruleText,
            interpretation: buildInterpretation(metricId, 'insufficientData', profile),
            warnings: annual.length
                ? [...warnings, `Only ${annual.length} valid annual observation(s); ${profile.requiredWindow} are required for a sector score.`]
                : warnings,
            unit: spec.unit,
        });
    }

    const status = applyThreshold(primaryValue, spec) || 'watch';
    const volatilityWarning = isVolatile(latestValue, primaryValue, spec);
    if (volatilityWarning) {
        warnings.push('Latest year diverges from the multi-year median; treat the latest reading as context, not as the sector score.');
    }

    return {
        metricId,
        label: metricLabel(metricId),
        status,
        latestStatus,
        primaryValue,
        latestValue,
        median3,
        median5,
        min,
        max,
        validAnnualCount: annual.length,
        positiveYearCount,
        positiveYearRatio,
        windowUsed: profile.primaryWindowLabel,
        ruleText,
        interpretation: buildInterpretation(metricId, status, profile),
        warnings,
        volatilityWarning,
        source: BENCHMARK_SOURCE_HEURISTIC,
        unit: spec.unit,
        notApplicableReason: null,
    };
}

function parseRatioNumber(raw) {
    if (raw == null || raw === '') return null;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
    const cleaned = String(raw).replace(/[,\s₹]/g, '').replace(/%/g, '').replace(/[^\d.-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
}

export function seriesFromRatio(ratio) {
    const list = Array.isArray(ratio?.values) ? ratio.values : [];
    return {
        values: list.map((item) => parseRatioNumber(item?.value)),
        years: list.map((item) => String(item?.year ?? item?.displayYear ?? '')),
    };
}

export function dedupeRatiosByName(ratios) {
    const seen = new Set();
    const result = [];
    (Array.isArray(ratios) ? ratios : []).forEach((ratio) => {
        const name = ratio?.name;
        if (!name || seen.has(name)) return;
        seen.add(name);
        result.push(ratio);
    });
    return result;
}

export function evaluateBalanceSheetRatiosPanel({
    sector,
    ratios = [],
} = {}) {
    const profile = getBalanceSheetBenchmarkProfile(sector);
    const displayRatios = dedupeRatiosByName(ratios);
    const byRatioName = {};

    Object.entries(BALANCE_SHEET_RATIOS_PANEL_MAP).forEach(([ratioName, mapping]) => {
        if (mapping.kind === 'notScored') {
            byRatioName[ratioName] = {
                kind: 'notScored',
                reason: mapping.reason,
                evaluation: null,
            };
            return;
        }
        const row = displayRatios.find((ratio) => ratio?.name === ratioName);
        const series = row ? seriesFromRatio(row) : { values: [], years: [] };
        byRatioName[ratioName] = {
            kind: 'scored',
            metricId: mapping.metricId,
            evaluation: evaluateBalanceSheetMetric({
                metricId: mapping.metricId,
                sector,
                values: series.values,
                years: series.years,
            }),
        };
    });

    return {
        profile,
        byRatioName,
        displayRatios,
        methodologyNote: METHODOLOGY_NOTE,
        source: BENCHMARK_SOURCE_HEURISTIC,
        scoredMetricIds: SCORED_METRIC_IDS,
    };
}

export function evaluateBalanceSheetComparisonBenchmarks() {
    return null;
}
